// Экран настроек (SPEC §4.6, FR-SETTINGS/FR-NOTIF/FR-AUTH-05/06/07). Профиль (updateMe), уведомления
// (NotifySettings — mode/sound/preview), тема (ThemePreference), сессии (list/revoke/logoutAll),
// установка/пуши PWA. Сетевое — через transport.rest (не-стор-данные, §4.0).
//
// R2-пробел: смена пароля «у себя» (FR-AUTH-07) НЕ имеет метода в RestClient client-transport (есть
// только adminResetPassword для админ-канала). Форма показана отключённой с пометкой — закрывается
// добавлением `POST /auth/change-password` в shared-contracts/client-transport (кандидат R2).

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import type { SessionInfo } from '@tele-chat/shared-contracts'
import { Avatar, Button, IconButton, ImageCropper } from '@tele-chat/design-system'
import { useRest } from '../transport/TransportContext.js'
import { useSession } from '../auth/SessionContext.js'
import { useThemePreference } from '../app/ThemePreferenceContext.js'
import { useNotifySettings } from '../settings/NotifySettingsContext.js'
import { usePwa } from '../pwa/PwaContext.js'
import { useNotifications } from '../notifications/NotificationsContext.js'
import { strings } from '../i18n/strings.js'
import { profileErrorText } from '../i18n/apiErrors.js'

const section: CSSProperties = {
  padding: 'var(--space-4)',
  borderBottom: '1px solid var(--color-divider)',
}
const sectionTitle: CSSProperties = {
  fontSize: 'var(--font-sm)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  marginBottom: 'var(--space-2)',
}
const row: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) 0',
}

export function SettingsScreen(): JSX.Element {
  const rest = useRest()
  const navigate = useNavigate()
  const { self, refresh, logout } = useSession()
  const { preference, setPreference } = useThemePreference()
  const { settings, update: updateNotify } = useNotifySettings()
  const pwa = usePwa()
  const { notify } = useNotifications()

  const [name, setName] = useState(self?.name ?? '')
  const [title, setTitle] = useState(self?.title ?? '')
  const [department, setDepartment] = useState(self?.department ?? '')
  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [pushBusy, setPushBusy] = useState(false)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [avatarBusy, setAvatarBusy] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setName(self?.name ?? '')
    setTitle(self?.title ?? '')
    setDepartment(self?.department ?? '')
  }, [self])

  useEffect(() => {
    void rest
      .listSessions()
      .then(setSessions)
      .catch(() => setSessions([]))
  }, [rest])

  // Смена аватара профиля: выбор файла → кроп (ImageCropper) → на сервер уходит JPEG 512×512
  // (~десятки КБ) — лимит размера недостижим, HEIC декодирует браузер (тот же механизм, что у группы).
  const onAvatarCropped = useCallback(
    async (blob: Blob): Promise<void> => {
      setCropFile(null)
      setAvatarBusy(true)
      try {
        await rest.setAvatar(blob)
        await refresh() // self.avatarUrl обновится (?v= — новая версия)
        notify({ kind: 'info', title: strings.settings.avatarUpdated })
      } catch (err) {
        notify({ kind: 'error', title: profileErrorText(err) })
      } finally {
        setAvatarBusy(false)
      }
    },
    [rest, refresh, notify],
  )

  const saveProfile = useCallback(async (): Promise<void> => {
    // Пустое имя сервер отвергнет (VALIDATION) — говорим сразу, без запроса.
    if (name.trim().length === 0) {
      notify({ kind: 'error', title: strings.settings.nameEmpty })
      return
    }
    try {
      await rest.updateMe({ name, title, department })
      await refresh()
      notify({ kind: 'info', title: strings.settings.saved })
    } catch (err) {
      // Причина отказа (пустое/длинное поле) вместо безликого «Не удалось выполнить действие» —
      // тестеры 02.07 трижды повторяли PATCH вслепую, не понимая, что не так.
      notify({ kind: 'error', title: profileErrorText(err) })
    }
  }, [rest, name, title, department, refresh, notify])

  const revoke = useCallback(
    async (id: string): Promise<void> => {
      try {
        await rest.revokeSession(id)
        setSessions((list) => list.filter((s) => s.id !== id))
      } catch {
        notify({ kind: 'error', title: strings.errors.actionFailed })
      }
    },
    [rest, notify],
  )

  const logoutEverywhere = useCallback(async (): Promise<void> => {
    try {
      await rest.logoutAll()
    } finally {
      navigate('/')
    }
  }, [rest, navigate])

  // Включить/выключить push на этом устройстве (по жесту). После действия перечитываем факт подписки,
  // чтобы кнопка и блок настроек сразу отразили новое состояние. Ошибку/причину отказа показываем тостом.
  const togglePush = useCallback(async (): Promise<void> => {
    setPushBusy(true)
    try {
      if (pwa.pushEnabled) {
        await pwa.pwa?.disablePush()
      } else {
        const res = await pwa.pwa?.enablePush()
        if (res && !res.ok) {
          const msg =
            res.reason === 'denied'
              ? strings.notif.pushDenied
              : res.reason === 'ios-needs-install'
                ? strings.notif.iosInstallFirst
                : res.reason === 'unsupported'
                  ? strings.notif.pushUnsupported
                  : strings.errors.actionFailed
          notify({ kind: 'error', title: msg })
        }
      }
    } catch {
      notify({ kind: 'error', title: strings.errors.actionFailed })
    } finally {
      await pwa.refreshPush()
      setPushBusy(false)
    }
  }, [pwa, notify])

  const header: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    borderBottom: '1px solid var(--color-divider)',
    background: 'var(--color-surface)',
  }

  return (
    <div data-screen="settings" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={header}>
        <IconButton icon="back" label={strings.nav.back} onClick={() => navigate('/')} />
        <span style={{ flex: 1, fontSize: 'var(--font-lg)', fontWeight: 'var(--weight-semibold)' }}>
          {strings.nav.settings}
        </span>
        {self?.accountRole === 'admin' ? (
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            {strings.nav.admin}
          </Button>
        ) : null}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* — профиль — */}
        <div data-settings-section="profile" style={section}>
          <div style={sectionTitle}>{strings.settings.profile}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-2) 0' }}>
            <Avatar name={self?.name ?? ''} src={self?.avatarUrl} size={64} colorKey={self?.id} />
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              data-profile-avatar-input=""
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f !== undefined) setCropFile(f)
                e.target.value = '' // повторный выбор того же файла снова даёт change
              }}
            />
            <Button variant="ghost" data-profile-avatar-change="" disabled={avatarBusy} onClick={() => avatarInputRef.current?.click()}>
              {strings.settings.changeAvatar}
            </Button>
          </div>
          <LabeledInput label={strings.auth.name} value={name} onChange={setName} />
          <LabeledInput label={strings.auth.title} value={title} onChange={setTitle} />
          <LabeledInput label={strings.auth.department} value={department} onChange={setDepartment} />
          <Button variant="primary" onClick={() => void saveProfile()} style={{ marginTop: 'var(--space-2)' }}>
            {strings.settings.save}
          </Button>
        </div>

        {/* — уведомления — */}
        <div data-settings-section="notifications" style={section}>
          <div style={sectionTitle}>{strings.settings.notifications}</div>

          {/* Статус + переключатель включения. Источник истины — факт активной подписки на этом
              устройстве (pwa.pushEnabled), а не только разрешение: кнопка и индикатор его отражают. */}
          {pwa.pushSupported ? (
            <div style={row}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flex: '0 0 auto',
                    background: pwa.pushEnabled ? 'var(--color-success, #34c759)' : 'var(--color-text-muted)',
                  }}
                />
                <span>{pwa.pushEnabled ? strings.notif.pushOn : strings.notif.pushOff}</span>
              </span>
              <Button
                variant={pwa.pushEnabled ? 'secondary' : 'primary'}
                data-enable-push=""
                data-push-enabled={pwa.pushEnabled ? '' : undefined}
                disabled={pushBusy}
                onClick={() => void togglePush()}
              >
                {pwa.pushEnabled ? strings.notif.disablePush : strings.notif.enablePush}
              </Button>
            </div>
          ) : (
            <div style={{ ...row, color: 'var(--color-text-muted)', fontSize: 'var(--font-sm)' }}>
              {strings.notif.pushUnsupported}
            </div>
          )}

          {/* Почему параметры недоступны, если пуши выключены. */}
          {pwa.pushSupported && !pwa.pushEnabled ? (
            <div style={{ fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)', paddingBottom: 'var(--space-2)' }}>
              {pwa.pushPermission === 'denied' ? strings.notif.pushDenied : strings.notif.enableFirst}
            </div>
          ) : null}

          {/* Параметры уведомлений активны ТОЛЬКО при включённых пушах: fieldset[disabled] нативно
              гасит инпуты (клики не проходят → нет PATCH notify-settings без подписки → нет 404). */}
          <fieldset
            data-notify-settings=""
            disabled={!pwa.pushEnabled}
            style={{
              border: 'none',
              margin: 0,
              padding: 0,
              opacity: pwa.pushEnabled ? 1 : 0.45,
              transition: 'opacity 120ms ease',
            }}
          >
            <div style={row}>
              <span>{strings.notif.sound}</span>
              <Toggle checked={settings.sound} disabled={!pwa.pushEnabled} onChange={(v) => void updateNotify({ sound: v })} name="sound" />
            </div>
            <div style={row}>
              <span>{strings.notif.preview}</span>
              <Toggle checked={settings.preview} disabled={!pwa.pushEnabled} onChange={(v) => void updateNotify({ preview: v })} name="preview" />
            </div>
            <div style={row}>
              <span>{strings.notif.modeMentions}</span>
              <Toggle
                checked={settings.mode === 'mentions'}
                disabled={!pwa.pushEnabled}
                onChange={(v) => void updateNotify({ mode: v ? 'mentions' : 'all' })}
                name="mentions"
              />
            </div>
          </fieldset>
        </div>

        {/* — оформление — */}
        <div data-settings-section="appearance" style={section}>
          <div style={sectionTitle}>{strings.settings.appearance}</div>
          <div style={row}>
            <span>{strings.settings.theme}</span>
            <select
              data-theme-select=""
              value={preference}
              onChange={(e) => setPreference(e.target.value as 'light' | 'dark' | 'system')}
              style={{ padding: '6px', borderRadius: 'var(--radius-control)' }}
            >
              <option value="light">{strings.settings.themeLight}</option>
              <option value="dark">{strings.settings.themeDark}</option>
              <option value="system">{strings.settings.themeSystem}</option>
            </select>
          </div>
          {pwa.canInstall ? (
            <div style={row}>
              <span>{strings.notif.install}</span>
              <Button variant="secondary" data-install="" onClick={() => void pwa.pwa?.promptInstall()}>
                {strings.notif.install}
              </Button>
            </div>
          ) : null}
        </div>

        {/* — сессии — */}
        <div data-settings-section="sessions" style={section}>
          <div style={sectionTitle}>{strings.settings.sessions}</div>
          {sessions.map((s) => (
            <div key={s.id} data-session={s.id} style={row}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--font-md)' }}>{s.deviceLabel}</span>
                <span style={{ display: 'block', fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
                  {s.ip} {s.current ? '• текущее устройство' : ''}
                </span>
              </span>
              {!s.current ? (
                <Button variant="ghost" onClick={() => void revoke(s.id)}>
                  {strings.settings.revoke}
                </Button>
              ) : null}
            </div>
          ))}
          <Button variant="danger" data-logout-all="" onClick={() => void logoutEverywhere()} style={{ marginTop: 'var(--space-2)' }}>
            {strings.settings.logoutAll}
          </Button>
          <Button variant="ghost" data-logout-here="" onClick={() => void logout()} style={{ marginLeft: 'var(--space-2)' }}>
            {strings.settings.logoutHere}
          </Button>
        </div>

        {/* — смена пароля (FR-AUTH-07): подтверждение старым + новый по политике длины — */}
        <div data-settings-section="password" style={section}>
          <div style={sectionTitle}>{strings.settings.changePassword}</div>
          <ChangePasswordForm />
        </div>
      </div>
      <ImageCropper
        file={cropFile}
        onCancel={() => setCropFile(null)}
        onCropped={(b) => void onAvatarCropped(b)}
        labels={{
          title: strings.groupInfo.cropTitle,
          done: strings.groupInfo.cropDone,
          cancel: strings.composer.cancel,
          zoom: strings.groupInfo.cropZoom,
        }}
      />
    </div>
  )
}

function LabeledInput({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'password'
  autoComplete?: string
}): JSX.Element {
  return (
    <label style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
      <span style={{ display: 'block', fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>{label}</span>
      <input
        data-field={label}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px',
          borderRadius: 'var(--radius-control)',
          border: '1px solid var(--color-divider)',
          background: 'var(--color-surface)',
          color: 'var(--color-text)',
        }}
      />
    </label>
  )
}

/** Форма смены своего пароля (FR-AUTH-07). Подтверждение старым + совпадение нового с повтором;
 *  сеть — transport.rest.changePassword (сервер верифицирует старый и проверяет политику длины). */
function ChangePasswordForm(): JSX.Element {
  const rest = useRest()
  const { notify } = useNotifications()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = useCallback(async (): Promise<void> => {
    if (next !== confirm) {
      notify({ kind: 'error', title: strings.settings.passwordMismatch })
      return
    }
    setBusy(true)
    try {
      await rest.changePassword({ oldPassword: current, newPassword: next })
      notify({ kind: 'info', title: strings.settings.passwordChanged })
      setCurrent('')
      setNext('')
      setConfirm('')
    } catch {
      // VALIDATION (неверный текущий / слишком короткий) — инлайн-ошибка, без модалки релогина.
      notify({ kind: 'error', title: strings.settings.passwordChangeFailed })
    } finally {
      setBusy(false)
    }
  }, [rest, notify, current, next, confirm])

  const canSubmit = current.length > 0 && next.length > 0 && confirm.length > 0 && !busy

  return (
    <div data-change-password="">
      <LabeledInput
        label={strings.settings.currentPassword}
        value={current}
        onChange={setCurrent}
        type="password"
        autoComplete="current-password"
      />
      <LabeledInput
        label={strings.settings.newPassword}
        value={next}
        onChange={setNext}
        type="password"
        autoComplete="new-password"
      />
      <LabeledInput
        label={strings.settings.confirmPassword}
        value={confirm}
        onChange={setConfirm}
        type="password"
        autoComplete="new-password"
      />
      <Button
        variant="primary"
        data-change-password-submit=""
        disabled={!canSubmit}
        onClick={() => void submit()}
        style={{ marginTop: 'var(--space-2)' }}
      >
        {strings.settings.changePassword}
      </Button>
    </div>
  )
}

function Toggle({
  checked,
  onChange,
  name,
  disabled = false,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  name: string
  disabled?: boolean
}): JSX.Element {
  return (
    <input
      type="checkbox"
      data-toggle={name}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ width: '20px', height: '20px', cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
  )
}
